import { ItemId, TreeDestinationPosition } from '@atlaskit/tree';
import { Button, Checkbox, Dialog } from '@fluentui/react-northstar';
import { Communication } from '@fluentui/react-teams';
import { Providers } from '@microsoft/mgt-element/dist/es6/providers/Providers';
import React, { useCallback, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { MobileSettingsView } from '../automation/AutomationTable';
import { PickersPageWrapper } from '../kb/PickersPageWrapper';
import { getCachedFeature } from '../shared/cache/FeatureCache';
import { Breadcrumbs } from '../shared/components/BreadcrumbNavigation';
import { CheckLogin } from '../shared/components/CheckLogin';
import { LoadingScreen } from '../shared/components/LoadingScreen';
import { ResultCount } from '../shared/components/ResultCount';
import {
  CTreeItem,
  TreeData,
  TreeWrapper
} from '../shared/components/tree/Tree';
import { platformService } from '../shared/services/platform.service';
import {
  ARCHIVE_CONFIRMATION_SUBTITLE,
  ARCHIVE_MODAL_HEADER,
  BUTTONS,
  CONTACT_ADMIN,
  DEFAULT_API_STATUS,
  DONE,
  ERROR,
  FILTERS_STRINGS,
  LOADING,
  PRODUCT_LICENSE,
  REFRESH_SESSION,
  SETTINGS,
  SOMETHING_WENT_WRONG,
  TREE_LISTING_CONSTANTS,
  UNARCHIVE_MODAL_HEADER,
  UNAUTHORIZED_ACCESS
} from '../shared/utils/constants';
import { toTitleCase } from '../shared/utils/helper';
import { TemplatePickerBaseProps } from '../templates/CustomComponents/DataSource/DataSourceForm';
import { EnumEdit } from './EnumEdit';

interface IENumList{
  typeName: string;
  updateData: (param:any) => Promise<void>;
  createData: (param?:any) => Promise<any>;
  getList: (param?:string) => Promise<any>;
  entityName: string;
  pageTitle: string;
  title: string;
  successMgs: string;
  featchingListMsg: string;
  updateMsg:string;
  userRoleHasAccess:boolean;
  dataSourceProps?: TemplatePickerBaseProps;
}

export const EnumList = (props:IENumList) => {
  const {t} = useTranslation();
  const api = new platformService();
  const [dialog, setDialog] = useState(false);
  const [modalHeader, setModalHeader] = useState('');
  const [newOrEditDialog, setNewOrEditDialog] = useState(false);
  const [dialogContent, setDialogContent] = useState('');
  const [archiveItem, setArchiveItem] =
    useState<MaybeNull<CategoryDetail>>(null);
  const [enumList, setEnumList] = useState<TreeData>({
    rootId: '0',
    items: {
      '0': {
        id: '0',
        children: [],
        hasChildren: true,
        container: true,
        isExpanded: true,
        isChildrenLoading: false,
        data: {
          title: 'root'
        }
      }
    }
  });
  const {dataSourceProps} = props;
  const isFromDataSourceStatus = dataSourceProps?.forDataSource ?? false;
  const [apiStatus, setAPIStatus] = useState<APIStatus>(
    DEFAULT_API_STATUS as APIStatus
  );
  const [isAddNewEnabled, setAddNewEnabled] = useState(false);
  const [isDragEnabled, setDragEnabled] = useState(false);
  
  const [isLicensedUser, setIsLicensedUser] = useState(false);
  const [isEditEnabled, setEditEnabled] = useState(false);
  const updateAPIStatus = (newState: Partial<APIStatus>) => {
    setAPIStatus({ ...apiStatus, ...newState });
  };

  const sortTreeItems = (items: CTreeItem[]) =>
    Object.values(items).sort((a, b) => a.data.Position - b.data.Position);

  const checkSortedTreeExistItems = (
    items: CTreeItem[],
    rootChild: string[]
  ) => {
    const sortedArray: string[] = [];
    items.forEach(child => {
      if (rootChild.includes(child.id as string)) {
        sortedArray.push(child.id as string);
      }
    });
    return sortedArray;
  };

  const fetchList = useCallback(async () => {
    updateAPIStatus({ msg: props.featchingListMsg });
    setArchiveItem(null);

    try {
      const permissionToEdit = JSON.parse(
        sessionStorage.getItem('my_application_roles')
      );
      const permissionToEditFiltered = permissionToEdit.filter(
        roles =>
          roles['TypeName'] == props.typeName
      );

      permissionToEditFiltered.find(p => {
        if (p.Permissions.includes('POST') || p.Permissions.includes('PUT')) {
          setAddNewEnabled(true);
          setDragEnabled(true);
          setEditEnabled(true);
          return true;
        }
      });

      const filterArchive = localStorage.getItem(`isArchived${props.entityName}View`) === "true" && !isFromDataSourceStatus ? "" : `${FILTERS_STRINGS.ARCHIVE_FALSE.substring(1, FILTERS_STRINGS.ARCHIVE_FALSE.length)} & `;
      const {
        data: { value }
      } = await props.getList(`?${filterArchive}v=${new Date().getTime()}`);
      const treeItems: CTreeItem[] = getTreeItems(value);
      const sortedArr = sortTreeItems(treeItems);
      const rootChilds = getRootChilds(treeItems);
      const sortedRootChilds = checkSortedTreeExistItems(sortedArr, rootChilds);

      setEnumList({
        ...enumList,
        // @ts-ignore
        items: {
          ...enumList.items,
          '0': {
            id: '0',
            children: [...sortedRootChilds],
            hasChildren: true,
            container: true,
            isExpanded: true,
            isChildrenLoading: false,
            data: {
              title: 'root'
            }
          },
          ...treeItems
        }
      });
      let cacheFeatureLicensedUser = await getCachedFeature(PRODUCT_LICENSE.AnalystAccess);
      setIsLicensedUser(cacheFeatureLicensedUser );
    } catch (err: any) {
      updateAPIStatus({
        status: ERROR,
        errCode: err.response.status
      });
    } finally {
      updateAPIStatus({ status: DONE });
    }
  }, []);

  const loadPage = () => {
    if  (props.userRoleHasAccess){
      fetchList();
    }
    else
    {
      setAPIStatus({ ...apiStatus, status: ERROR, errCode: 401 });
    }
    
  };

  const navs: Breadcrumbs = {
    breadcrumbs: [
      {
        title: toTitleCase(SETTINGS),
        link: '/settings'
      },
      {
        title: toTitleCase(props.title),
        link: ''
      }
    ]
  };

  const getTreeItems = useCallback(array => {
    const items = array.reduce((acc, item) => {
      const child = getChildsOfTree(array, item.Id);
      const sortedChild = child
        .sort((a, b) => a.position - b.position)
        .map(item => item.id.toString());
      return {
        ...acc,
        [item.Id.toString()]: {
          id: item.Id.toString(),
          children: [...sortedChild],
          hasChildren: child.length > 0,
          container: true,
          isExpanded: child.length > 0,
          isChildrenLoading: false,
          data: {
            title: item.Value,
            ...item
          }
        }
      };
    }, {} as CTreeItem);
    return items;
  }, []);

  const getChildsOfTree: (
    items: CTreeItem[],
    id: number | string
  ) => { id: number; position: number }[] = useCallback(
    (items: CTreeItem[], id: number | string) => {
      const child = [];
      for (let i = 0; i < items.length; i++) {
        const element: CTreeItem['data'] = items[i];
        if (element?.ParentId === id) {
          child.push({ id: element.Id.toString(), position: element.Position });
        }
      }
      return child;
    },
    []
  );

  const getRootChilds: (items: CTreeItem[]) => string[] = useCallback(
    (items: CTreeItem[]) => {
      const getChildOfRoot: string[] = [];

      Object.keys(items).forEach((key: string) => {
        getChildOfRoot.push(...items[key].children);
      });

      return Object.keys(items).filter(
        (val: string) => !getChildOfRoot.includes(val)
      );
    },
    []
  );

  const onDragHandler = async (
    source: ItemId,
    destination: TreeDestinationPosition,
    rootChildren
  ) => {
    if(isFromDataSourceStatus) return;
    let data: UpdateOnDrag = {
      Id: '',
      ParentId: '',
      Position: 0
    };

    const positionOfItem =
      enumList.items[destination.parentId.toString()].data.Position;
    let updatedRootChildren: UpdatePickerPositionPayload[] = [];
    updatedRootChildren = rootChildren.map((child, index) => ({
      '@odata.type': '#'+props.typeName,
      Id: child,
      Position: index + 1
    }));

    if (destination.parentId === '0') {
      data = {
        ...data,
        Id: source,
        ParentId: null
      };
    } else {
      data = {
        ...data,
        Id: source,
        ParentId: destination?.parentId,
        Position: positionOfItem
      };
    }
    try {
      await props.updateData(data);
      await api.updatePickerPositions(props.entityName, updatedRootChildren);
      toast.success(props.successMgs);
    } catch (error: any) {
      updateAPIStatus({
        status: ERROR,
        errCode: error.response.status
      });
    }
  };

  const onConfirmArchiveHandler = async () => {
    try {
      setDialog(false);
      updateAPIStatus({ msg: props.updateMsg, status: LOADING });
      await props.updateData({
        IsArchived: !archiveItem.IsArchived,
        IsDefault: false,
        Id: archiveItem.Id.toString()
      });
      fetchList();
    } catch (err: any) {
      updateAPIStatus({
        status: ERROR,
        errCode: err.response.status
      });
    }
  };

  const errConfig = {
    fields: {
      title:
        apiStatus.errCode === 401
          ? UNAUTHORIZED_ACCESS
          : SOMETHING_WENT_WRONG,
      desc: CONTACT_ADMIN
    }
  };

  const onArchiveSwitchChange = () => {
    const isArchived = localStorage.getItem(`isArchived${props.entityName}View`) === "true";
    localStorage.setItem(`isArchived${props.entityName}View`, !isArchived ? "true" : "false");
    loadPage();
  }

  return (
    <CheckLogin onSignedIn={loadPage}>
      <Helmet>
        <title>{props.pageTitle}</title>
      </Helmet>
      <div className="hidden md:block">
        {apiStatus.status === LOADING ? (
          <LoadingScreen message={apiStatus.msg} height={isFromDataSourceStatus ? '50vh' : ''}/>
        ) : apiStatus.status === ERROR ? (
          <>
            <Communication {...errConfig} />
            {apiStatus.errCode === 401 && (
              <Button
                content={REFRESH_SESSION}
                primary
                onClick={e => {
                  Providers.globalProvider.login();
                }}
              />
            )}
          </>
        ) : (
          <>
            <PickersPageWrapper
              title={toTitleCase(props.title)}
              breadcrumbs={navs}
              items={Object.entries(enumList.items).length}
              ifEmpty={{
                header: t('category.there-are-no-categories'),
                subHeader:
                t('category.get-started-by-clicking')
              }}
              isAddNewEnabled={isAddNewEnabled}
              onAddClickHandler={() => {
                setNewOrEditDialog(true);
                setArchiveItem(null);
              }}
              forDataSource={isFromDataSourceStatus}
            >
              <div>
                {!isFromDataSourceStatus && (
                <div className="flex flex-row-reverse mx-2 pb-2">
                  <Checkbox label={t('preferences.show-archived')} toggle onChange={onArchiveSwitchChange} checked={localStorage.getItem(`isArchived${props.entityName}View`) === "true"}/>
                </div>
                )}
                <TreeWrapper
                  depth={Object.entries(enumList.items).length}
                  data={enumList}
                  isEditEnabled={isEditEnabled}
                  onEditHandler={item => {
                    setArchiveItem(item.data);
                    setNewOrEditDialog(true);
                  }}
                  onArchiveHandler={item => {
                    setArchiveItem(item.data);
                    if (item.data.IsArchived) {
                      setModalHeader(
                        `${UNARCHIVE_MODAL_HEADER} ‘${item.data.title}’?`
                      );
                      setDialogContent(
                        `${ARCHIVE_CONFIRMATION_SUBTITLE} ${item.data.title} ${props.title}?`
                      );
                    } else {
                      setModalHeader(
                        `${ARCHIVE_MODAL_HEADER} ‘${item.data.title}’?`
                      );
                      setDialogContent(
                        `${ARCHIVE_CONFIRMATION_SUBTITLE} ${item.data.title} ${props.title}?`
                      );
                    }
                    setDialog(true);
                  }}
                  onDragEndHandler={onDragHandler}
                  isDragEnabled={isDragEnabled}
                  isLicensedUser={isLicensedUser}
                  dataSourceProps={dataSourceProps}
                />
                <div className="h-9 flex items-center"><ResultCount count={Object.entries(enumList.items).length - 1} /></div>
              </div>
            </PickersPageWrapper>
            <Dialog
              open={dialog}
              onCancel={() => setDialog(false)}
              cancelButton={toTitleCase(BUTTONS.CANCEL)}
              confirmButton={
                archiveItem?.IsArchived
                  ? TREE_LISTING_CONSTANTS.unArchive
                  : TREE_LISTING_CONSTANTS.archive
              }
              onConfirm={onConfirmArchiveHandler}
              content={dialogContent}
              header={modalHeader}
              style={{ maxWidth: '500px' }}
            />
            <EnumEdit
              id={archiveItem?.Id.toString()}
              open={newOrEditDialog}
              onCancel={() => setNewOrEditDialog(false)}
              onSuccessFullCall={fetchList}
              updateData={props.updateData}
              createData={props.createData}
              getList={props.getList}
              title={props.title}
            />
          </>
        )}
      </div>
      <MobileSettingsView />
    </CheckLogin>
  );
};
